import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Palette, ArrowRight } from "lucide-react";

import mushroom from "@/assets/flakes/mushroon.jpg.asset.json";
import dovetail from "@/assets/flakes/dovetail.jpg.asset.json";
import basalt from "@/assets/flakes/basatt.jpg.asset.json";
import wombat from "@/assets/flakes/wombat.jpg.asset.json";
import nightfall from "@/assets/flakes/nightfall.jpg.asset.json";
import raven from "@/assets/flakes/raven.jpg.asset.json";
import lucas from "@/assets/flakes/lucas.jpg.asset.json";
import tidalWave from "@/assets/flakes/tidal-wave.jpg.asset.json";
import stonewash from "@/assets/flakes/stonewash.jpg.asset.json";
import orbit from "@/assets/flakes/orbit.jpg.asset.json";
import shoreline from "@/assets/flakes/shoreline.jpg.asset.json";
import outback from "@/assets/flakes/outback.jpg.asset.json";
import waxwing from "@/assets/flakes/waxwing.jpg.asset.json";
import sparrow from "@/assets/flakes/sparrow.jpg.asset.json";
import dolerite from "@/assets/flakes/dotrite.jpg.asset.json";

export const Route = createFileRoute("/color-chart")({
  head: () => ({
    meta: [
      { title: "Flake Color Chart | Epoxy Floor Colors, Blanco Pro" },
      { name: "description", content: "Browse our full epoxy flake color chart, Mushroom, Dovetail, Basalt, Raven, Lucas, Tidal Wave, Orbit and more. Pick the perfect finish for your NJ floor." },
      { property: "og:title", content: "Epoxy Flake Color Chart, Blanco Pro" },
      { property: "og:description", content: "Explore every flake blend we install across NJ residential, commercial and industrial floors." },
      { property: "og:url", content: "https://blancopros.com/color-chart" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Epoxy Flake Color Chart, Blanco Pro" },
      { name: "twitter:description", content: "Explore every flake blend we install across NJ residential, commercial and industrial floors." },
    ],
    links: [{ rel: "canonical", href: "https://blancopros.com/color-chart" }],
  }),
  component: ColorChartPage,
});

type Flake = {
  name: string;
  slug: string;
  img: string;
  palette: string[];
  family: "Neutral" | "Greyscale" | "Blues" | "Bold" | "Earthy";
  vibe: string;
  pairs: string;
};

const flakes: Flake[] = [
  { name: "Mushroom", slug: "mushroom", img: mushroom.url, palette: ["#3f3f3e","#6f6e6a","#a36a3a","#a8a39c","#e5e1d6"], family: "Neutral", vibe: "Warm, organic, earthy", pairs: "Garages · Living spaces · Showrooms" },
  { name: "Dovetail", slug: "dovetail", img: dovetail.url, palette: ["#5a504c","#7a6f6a","#9a9491","#b8b3af","#d4cfca"], family: "Neutral", vibe: "Soft greige, modern", pairs: "Basements · Retail · Salons" },
  { name: "Basalt", slug: "basalt", img: basalt.url, palette: ["#3a3d4d","#525668","#8d92a0","#b9bcc5","#d6d8de"], family: "Greyscale", vibe: "Cool stone, refined", pairs: "Garages · Lobbies · Offices" },
  { name: "Wombat", slug: "wombat", img: wombat.url, palette: ["#1a1a1a","#6e6e6e","#a4a4a8","#cfcfcf","#eaeaea"], family: "Greyscale", vibe: "Classic monochrome", pairs: "Garages · Workshops · Gyms" },
  { name: "Nightfall", slug: "nightfall", img: nightfall.url, palette: ["#1c1c1c","#2c2e35","#5e6168","#7e8086","#a6a8ab"], family: "Greyscale", vibe: "Deep, dramatic charcoal", pairs: "Showrooms · Garages · Mancaves" },
  { name: "Raven", slug: "raven", img: raven.url, palette: ["#181818","#3a3a3a","#b9bcbf","#cfd2d5","#ececec"], family: "Bold", vibe: "High-contrast tuxedo", pairs: "Retail · Studios · Modern garages" },
  { name: "Lucas", slug: "lucas", img: lucas.url, palette: ["#1d2c5d","#27397a","#5a76b6","#b0b3b8","#d4d6d9"], family: "Blues", vibe: "Crisp navy & silver", pairs: "Mancaves · Sports rooms · Garages" },
  { name: "Tidal Wave", slug: "tidal-wave", img: tidalWave.url, palette: ["#3c4a5e","#56697e","#94a3b6","#bdbfc1","#dadcde"], family: "Blues", vibe: "Coastal, calming", pairs: "Beach homes · Patios · Spas" },
  { name: "Stonewash", slug: "stonewash", img: stonewash.url, palette: ["#1f2f5e","#445a76","#7a8da6","#9c9690","#cfd0cf"], family: "Blues", vibe: "Denim & stone", pairs: "Garages · Basements · Cafés" },
  { name: "Orbit", slug: "orbit", img: orbit.url, palette: ["#0e0e0e","#1c47a6","#9097a2","#dcdde2","#ffffff"], family: "Bold", vibe: "Electric, sporty", pairs: "Showrooms · Gyms · Auto bays" },
  { name: "Shoreline", slug: "shoreline", img: shoreline.url, palette: ["#1f1f1d","#a07a44","#a99977","#c8bfa8","#ece6d6"], family: "Earthy", vibe: "Soft sand & driftwood", pairs: "Beach homes · Patios · Sunrooms" },
  { name: "Outback", slug: "outback", img: outback.url, palette: ["#1f1f1c","#5a4a4c","#b07a52","#d9b88a","#d8d8d8"], family: "Earthy", vibe: "Desert tones, rustic", pairs: "Workshops · Cabins · Garages" },
  { name: "Waxwing", slug: "waxwing", img: waxwing.url, palette: ["#5e5b5d","#9a9491","#c4683a","#d4a587","#ece8de"], family: "Earthy", vibe: "Terracotta & stone", pairs: "Studios · Cafés · Living rooms" },
  { name: "Sparrow", slug: "sparrow", img: sparrow.url, palette: ["#1f2a3a","#7e8294","#653a3a","#a16842","#9a8a72"], family: "Bold", vibe: "Rustic, moody mix", pairs: "Mancaves · Lounges · Garages" },
  { name: "Dolerite", slug: "dolerite", img: dolerite.url, palette: ["#2c2c30","#5a5560","#8a8689","#a89a8c","#bdb9b6"], family: "Greyscale", vibe: "Quarried stone texture", pairs: "Industrial · Lofts · Warehouses" },
];

const families = ["All", "Neutral", "Greyscale", "Blues", "Earthy", "Bold"] as const;

function ColorChartPage() {
  return (
    <>
      {/* HERO */}
      <section className="pt-40 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-hero relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-gradient-gold blur-3xl opacity-25 animate-float" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-gradient-gold blur-3xl opacity-15 animate-float" style={{ animationDelay: "2s" }} />

        <div className="relative max-w-6xl mx-auto">
          <nav className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-foreground transition-colors">Services</Link>
            <span>/</span>
            <span className="text-foreground">Color Chart</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 animate-fade-up">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-4">
                <Palette className="w-4 h-4" /> Flake Color Library
              </div>
              <h1 className="font-display text-5xl md:text-7xl font-bold leading-[0.95] mb-6">
                Pick the <em className="not-italic bg-gradient-gold bg-clip-text text-transparent">finish</em><br />
                you'll fall in love with.
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl">
                Every floor we install is a one-of-a-kind blend of decorative flakes. Explore our most-requested colorways below, each engineered for durability, slip-resistance, and timeless style.
              </p>
            </div>

            {/* Marquee preview strip */}
            <div className="lg:col-span-4 hidden lg:block">
              <div className="grid grid-cols-2 gap-3">
                {flakes.slice(0, 4).map((f, i) => (
                  <div
                    key={f.slug}
                    className="aspect-square rounded-2xl overflow-hidden shadow-elegant border border-border animate-fade-up"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <img src={f.img} alt={`${f.name} epoxy flake blend`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLOR GRID */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-2">{flakes.length} Signature Blends</div>
                <h2 className="font-display text-4xl md:text-5xl font-bold">The full collection.</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {families.map((f) => (
                  <span key={f} className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] border border-border bg-card text-muted-foreground">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {flakes.map((f, i) => (
              <Reveal key={f.slug} delay={(i % 3) * 100}>
                <article className="group relative rounded-3xl overflow-hidden bg-card border border-border card-tilt">
                  {/* Image */}
                  <div className="aspect-[4/5] overflow-hidden relative">
                    <img
                      src={f.img}
                      alt={`${f.name} epoxy flake blend`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-90" />

                    {/* Family chip */}
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] bg-background/80 backdrop-blur border border-border text-foreground">
                      {f.family}
                    </span>

                    {/* Name */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-display text-3xl md:text-4xl font-bold mb-1">{f.name}</h3>
                      <p className="text-sm text-muted-foreground">{f.vibe}</p>
                    </div>
                  </div>

                  {/* Palette swatches */}
                  <div className="p-6 pt-5">
                    <div className="flex gap-1.5 mb-4">
                      {f.palette.map((c, idx) => (
                        <div
                          key={idx}
                          className="flex-1 h-10 rounded-md border border-border/50 shadow-sm transition-transform group-hover:-translate-y-0.5"
                          style={{ backgroundColor: c, transitionDelay: `${idx * 30}ms` }}
                          title={c}
                        />
                      ))}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Best for</div>
                    <p className="text-sm text-foreground/80">{f.pairs}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTE */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">A note on color</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">Every floor is hand-broadcast.</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Because each system is mixed and broadcast by hand on site, the final floor will have natural variation from the samples shown. Lighting, base coat, and flake density all play a role. We bring physical samples with us during your on-site evaluation visit so you can see and feel the real finish in your own space. We don't ship samples, they're only delivered in person during the walk-through.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-foreground text-background px-7 py-3.5 rounded-full font-medium hover:bg-gradient-gold hover:text-gold-foreground transition-all shadow-soft hover:shadow-gold"
            >
              Book an evaluation visit
              <ArrowRight className="w-4 h-4" />

            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
